import { useContext } from 'react';
import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { AccountFiltersContext } from 'Stores/AccountFiltersStore/AccountFiltersContext';
import {
	getBasicAccountFiltersStore,
	getPurchasesCompleteAccountFiltersStore,
} from 'Stores/AccountFiltersStore/AccountFiltersStore.helpers';
import type {
	AccountFiltersStore,
	InitialAccountFiltersStoreValues,
} from 'Stores/AccountFiltersStore/AccountFiltersStore.types';
import { createGenericStoreWithDevtools } from 'Stores/store';
import { AccountCategory, AccountSlugs } from 'Types/account';
import { isDev } from 'Utilities/helpers';

const createAccountFiltersStore = (initialValues?: Partial<InitialAccountFiltersStoreValues>) =>
	devtools<AccountFiltersStore>(
		(set) => ({
			[AccountCategory.BIDS]: {
				[AccountSlugs.ACTIVE]: getBasicAccountFiltersStore(
					set,
					AccountCategory.BIDS,
					AccountSlugs.ACTIVE,
					initialValues?.[AccountCategory.BIDS]?.[AccountSlugs.ACTIVE],
				),
				[AccountSlugs.DIDNT_WIN]: getBasicAccountFiltersStore(
					set,
					AccountCategory.BIDS,
					AccountSlugs.DIDNT_WIN,
					initialValues?.[AccountCategory.BIDS]?.[AccountSlugs.DIDNT_WIN],
				),
				[AccountSlugs.UNDER_OFFER]: getBasicAccountFiltersStore(
					set,
					AccountCategory.BIDS,
					AccountSlugs.UNDER_OFFER,
					initialValues?.[AccountCategory.BIDS]?.[AccountSlugs.UNDER_OFFER],
				),
			},
			[AccountCategory.PURCHASES]: {
				[AccountSlugs.CANCELLED]: getBasicAccountFiltersStore(
					set,
					AccountCategory.PURCHASES,
					AccountSlugs.CANCELLED,
					initialValues?.[AccountCategory.PURCHASES]?.[AccountSlugs.CANCELLED],
				),
				[AccountSlugs.COMPLETE]: getPurchasesCompleteAccountFiltersStore(
					set,
					AccountCategory.PURCHASES,
					AccountSlugs.COMPLETE,
					initialValues?.[AccountCategory.PURCHASES]?.[AccountSlugs.COMPLETE],
				),
			},
		}),
		{
			enabled: isDev,
			name: 'AccountFiltersStore',
		},
	);

export const createAccountFiltersStoreWithInitials = (
	initialValues?: Partial<InitialAccountFiltersStoreValues>,
): StoreApi<AccountFiltersStore> =>
	createGenericStoreWithDevtools<AccountFiltersStore>(createAccountFiltersStore(initialValues));

export const useAccountFiltersStore = <T,>(
	selector: (state: AccountFiltersStore) => T,
	equalityFn?: () => boolean,
): T => {
	const store = useContext(AccountFiltersContext);
	if (!store) {
		throw new Error('Missing AccountFiltersStore.Provider in the tree');
	}

	return useStore(store, selector, equalityFn);
};

// Bids & Offers selectors
export const useBidsAndOffersActive = (): AccountFiltersStore[AccountCategory.BIDS][AccountSlugs.ACTIVE] =>
	useAccountFiltersStore((state) => state[AccountCategory.BIDS][AccountSlugs.ACTIVE]);
export const useBidsAndOffersUnderOffer = (): AccountFiltersStore[AccountCategory.BIDS][AccountSlugs.UNDER_OFFER] =>
	useAccountFiltersStore((state) => state[AccountCategory.BIDS][AccountSlugs.UNDER_OFFER]);
export const useBidsAndOffersDidntWin = (): AccountFiltersStore[AccountCategory.BIDS][AccountSlugs.DIDNT_WIN] =>
	useAccountFiltersStore((state) => state[AccountCategory.BIDS][AccountSlugs.DIDNT_WIN]);

// Purchases selectors
export const usePurchasesComplete = (): AccountFiltersStore[AccountCategory.PURCHASES][AccountSlugs.COMPLETE] =>
	useAccountFiltersStore((state) => state[AccountCategory.PURCHASES][AccountSlugs.COMPLETE]);
export const usePurchasesCancelled = (): AccountFiltersStore[AccountCategory.PURCHASES][AccountSlugs.CANCELLED] =>
	useAccountFiltersStore((state) => state[AccountCategory.PURCHASES][AccountSlugs.CANCELLED]);
